import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api } from '../hooks/useAuth';
import usePermissions from '../hooks/usePermissions';
import Swal from 'sweetalert2';

const Users = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Fetch permission groups
  const { data: permissionGroups = [] } = useQuery({
    queryKey: ['permission-groups'],
    queryFn: async () => {
      const response = await api.get('/permission-groups');
      return response.data;
    }
  });

  // Validation schemas
  const userCreateSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    email: z.string().email('Invalid email'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    locale: z.enum(['de', 'en', 'tr', 'ru']).optional(),
    role: z.enum(['admin', 'user']).optional(),
    permissionGroupId: z.string().optional(),
  });

  const userUpdateSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    email: z.string().email('Invalid email'),
    password: z.string().optional().refine((val) => !val || val.length >= 6, {
      message: 'Password must be at least 6 characters'
    }),
    locale: z.enum(['de', 'en', 'tr', 'ru']).optional(),
    role: z.enum(['admin', 'user']).optional(),
    permissionGroupId: z.string().optional(),
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(editingUser ? userUpdateSchema : userCreateSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      locale: 'en',
      role: 'user',
      permissionGroupId: '',
    },
  });

  // Fetch users
  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await api.get('/users');
      return response.data;
    },
  });

  // Create user mutation
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const { password, ...userData } = data;
      const response = await api.post('/users', {
        ...userData,
        passwordHash: password
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowModal(false);
      reset();
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: t('users.userCreatedSuccess'),
        timer: 2000,
        showConfirmButton: false
      });
    },
    onError: (error) => {
      console.error('User creation error:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || t('users.userCreateFailed');
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: errorMessage,
        confirmButtonText: t('common.ok')
      });
    },
  });

  // Update user mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const { password, ...userData } = data;
      const updateData = password && password.trim() !== '' ? { ...userData, passwordHash: password } : userData;
      const response = await api.put(`/users/${id}`, updateData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowModal(false);
      setEditingUser(null);
      reset();
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: t('users.userUpdatedSuccess'),
        timer: 2000,
        showConfirmButton: false
      });
    },
    onError: (error) => {
      console.error('User update error:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || t('users.userUpdateFailed');
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: errorMessage,
        confirmButtonText: t('common.ok')
      });
    },
  });

  // Delete user mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/users/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: t('users.userDeletedSuccess'),
        timer: 2000,
        showConfirmButton: false
      });
    },
    onError: (error) => {
      console.error('User deletion error:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || t('users.userDeleteFailed');
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: errorMessage,
        confirmButtonText: t('common.ok')
      });
    },
  });

  const onSubmit = (data) => {
    console.log('User form submitted:', data);
    if (editingUser) {
      updateMutation.mutate({ id: editingUser._id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const onError = (errors) => {
    console.log('User form validation errors:', errors);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setValue('name', user.name);
    setValue('email', user.email);
    setValue('locale', user.locale);
    setValue('role', user.role);
    setValue('permissionGroupId', user.permissionGroupId || '');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    reset();
  };

  const getRoleColor = (role) => {
    return role === 'admin' 
      ? 'bg-red-100 text-red-800' 
      : 'bg-blue-100 text-blue-800';
  };

  const getLocaleFlag = (locale) => {
    const flags = {
      de: '🇩🇪',
      en: '🇬🇧',
      tr: '🇹🇷',
      ru: '🇷🇺',
    };
    return flags[locale] || '🌐';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('users.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('users.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn btn-primary"
        >
          {t('users.addUser')}
        </button>
      </div>

      {/* Users List */}
      <div className="card">
        {users && users.length > 0 ? (
          <div className="overflow-hidden">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('users.name')}</th>
                  <th>{t('users.email')}</th>
                  <th>{t('users.role')}</th>
                  <th>{t('users.locale')}</th>
                  <th>{t('users.created')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user._id}>
                    <td className="font-medium">{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="flex items-center">
                      <span className="mr-2">{getLocaleFlag(user.locale)}</span>
                      {user.locale.toUpperCase()}
                    </td>
                    <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(user)}
                          className="text-blue-600 hover:text-blue-500 text-sm"
                        >
                          {t('common.edit')}
                        </button>
                        {canDelete('users') && (
                          <button
                            onClick={() => {
                              Swal.fire({
                                title: t('users.confirmDelete'),
                                text: t('users.confirmDeleteText'),
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#d33',
                                cancelButtonColor: '#3085d6',
                                confirmButtonText: t('users.yesDelete'),
                                cancelButtonText: t('common.cancel')
                              }).then((result) => {
                                if (result.isConfirmed) {
                                  deleteMutation.mutate(user._id);
                                }
                              });
                            }}
                            disabled={deleteMutation.isPending}
                            className="text-red-600 hover:text-red-500 text-sm"
                          >
                            {t('common.delete')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-400 text-6xl mb-4">👥</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('users.noUsersFound')}</h3>
            <p className="text-gray-500 mb-4">{t('users.createFirstUser')}</p>
            <button
              onClick={() => setShowModal(true)}
              className="btn btn-primary"
            >
              {t('users.addUser')}
            </button>
          </div>
        )}
      </div>

      {/* User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingUser ? t('users.editUser') : t('users.addUser')}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit, onError)} className="space-y-4">
              <div>
                <label className="label">{t('users.name')}</label>
                <input
                  {...register('name')}
                  className={`input ${errors.name ? 'input-error' : ''}`}
                  placeholder={t('users.userNamePlaceholder')}
                />
                {errors.name && (
                  <p className="error-message">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="label">{t('users.email')}</label>
                <input
                  {...register('email')}
                  type="email"
                  className={`input ${errors.email ? 'input-error' : ''}`}
                  placeholder={t('users.emailPlaceholder')}
                />
                {errors.email && (
                  <p className="error-message">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="label">{t('users.password')} {editingUser && t('users.passwordOptionalInfo')}</label>
                <input
                  {...register('password')}
                  type="password"
                  className={`input ${errors.password ? 'input-error' : ''}`}
                  placeholder={editingUser ? t('users.passwordPlaceholder') : t('users.password')}
                />
                {errors.password && (
                  <p className="error-message">{errors.password.message}</p>
                )}
              </div>

              <div>
                <label className="label">{t('users.role')}</label>
                <select {...register('role')} className="input">
                  <option value="user">{t('users.user')}</option>
                  <option value="admin">{t('users.admin')}</option>
                </select>
              </div>

              <div>
                <label className="label">{t('users.locale')}</label>
                <select {...register('locale')} className="input">
                  <option value="en">🇬🇧 {t('users.english')}</option>
                  <option value="de">🇩🇪 {t('users.deutsch')}</option>
                  <option value="tr">🇹🇷 {t('users.turkish')}</option>
                  <option value="ru">🇷🇺 {t('users.russian')}</option>
                </select>
              </div>

              <div>
                <label className="label">{t('users.permissionGroup')}</label>
                <select {...register('permissionGroupId')} className="input">
                  <option value="">{t('users.selectPermissionGroup')}</option>
                  {permissionGroups.map((group) => (
                    <option key={group._id} value={group._id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn btn-secondary"
                >
{t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="btn btn-primary"
                >
                  {createMutation.isPending || updateMutation.isPending 
                    ? t('common.saving') 
                    : editingUser ? t('users.updateUser') : t('users.createUser')
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
